import { forwardRef, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Slide } from '@mui/material';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { parse } from 'papaparse';

import AlertMessage from '../alerts/AlertMessage';
import appSettings from '../../app-settings';
import { getNetworkError, uploadFile } from '../../core/utils';
import Button from '../buttons/Button';
import Stepper from '../stepper/Stepper';
import ImportStep from './ImportStep';
import CategorizeStep from './CategorizeStep';
import TagStep from './TagStep';
import { useTags } from '../../store/tags/hook';
import MatchStep from './MatchStep';
import OnboardingStep from './OnboardingStep';
import CompleteStep from './CompleteStep';
import SuccessStep from './SuccessStep';
import { useContactFields } from '../../store/contacts/hooks';
import { useImports } from '../../store/imports/hooks';
import { useAccount } from '../../store/account/hooks';
import { IMPORT_TYPES } from '../../core/constants';
import ImportTypeStep from './ImportTypeStep';

const CONTACTS_URL = `${appSettings.baseUrl}/contacts`;

const GENERAL_UPLOAD_STEPS = ['Type', 'Import'];

// Derived rather than hardcoded so it cannot drift from the labels below.
const IMPORT_CONTACTS_STEPS = ['Type', 'Import', 'Categorize', 'Tag', 'Match', 'Onboard', 'Complete'];

const IMPORT_SUPPRESSION_LIST_STEPS = ['Type', 'Import', 'Match', 'Complete'];

/**
 * Index of the file-upload step. Derived from the labels rather than hardcoded to 1
 * so inserting a step before it cannot silently point the "skip Type" seed at the
 * wrong screen. Both step lists share the same prefix, so either works.
 */
const IMPORT_FILE_STEP = IMPORT_CONTACTS_STEPS.indexOf('Import');

const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    parse(file, {
      header: true,
      complete: (results) => {
        resolve({ headers: results.meta.fields, data: results.data });
      },
      error: (error) => {
        reject(error);
      },
    });
  });

const createPresignedUploadUrl = async ({
  filename,
  skipUpdate,
  sendRecurringEmails,
  optInContacts,
  tagIds,
  tagValueMap,
  fieldMappings,
  importType,
}) => {
  const response = await axios.post(`${CONTACTS_URL}/import`, {
    filename,
    tag_ids: tagIds,
    tag_value_map: tagValueMap,
    skip_update: skipUpdate,
    send_recurring_emails: sendRecurringEmails,
    opt_in_contacts: optInContacts,
    field_mappings: fieldMappings,
    import_type: importType,
  });
  return response.data.url;
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FullScreenUpload = ({ open, onClose, preselectedTag }) => {
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const { error: tagError, setError: setTagError, tags } = useTags();
  const { getContactFields } = useContactFields();
  const { newImport, updateNewImport, goNext, getImports, resetNewImport } = useImports();
  const { account } = useAccount();

  useEffect(() => {
    getContactFields();
  }, [getContactFields]);

  const hasBeenOnboarded = !!account?.onboarded;

  /**
   * Opening with a tag preselected means the import type is already decided, so the
   * "Type" step has nothing to ask. Skip straight to file upload.
   *
   * `minStep` is raised alongside `activeStep` so ImportStep's Back button cannot
   * reverse into the step we skipped — setting the initial step alone would make the
   * skip cosmetic.
   *
   * Ref-guarded to run once per open: this must not re-fire and yank the user back to
   * the upload step after they have advanced.
   */
  const stepSeededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      stepSeededRef.current = false;
      return;
    }
    if (stepSeededRef.current || !preselectedTag) return;

    updateNewImport({
      importType: IMPORT_TYPES.contacts,
      activeStep: IMPORT_FILE_STEP,
      minStep: IMPORT_FILE_STEP,
    });
    stepSeededRef.current = true;
  }, [open, preselectedTag, updateNewImport]);

  /**
   * Seed the wizard's tag selection when it opens.
   *
   * Two independent sources, and they COEXIST because selectedTags is a list:
   *
   *  - `preselectedTag` — passed by the onboarding donor-group cards' "Import into
   *    this group", which is what makes those cards the dedicated-import-per-tag
   *    entry point rather than just a status report.
   *  - the deliverability warm-up tag, still auto-selected for a not-yet-onboarded
   *    account exactly as before.
   *
   * The spec originally said to STOP auto-selecting the warm-up tag here so the
   * tenant makes an explicit bucket choice. Left in place deliberately: nothing in
   * the codebase reads that tag by name (only its creation in onboarding-api and
   * this selection), so its purpose is operational rather than programmatic, and
   * dropping it would change what a new tenant's first import is tagged with for no
   * verifiable benefit. The donor bucket is now an explicit choice regardless.
   *
   * Also ref-guarded, for a subtler reason than the step seed: without it this
   * re-ran whenever the `tags` array identity changed and re-wrote `selectedTags`
   * wholesale, silently undoing a chip the user had removed in the Tag step.
   */
  const tagsSeededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      tagsSeededRef.current = false;
      return;
    }
    if (tagsSeededRef.current) return;

    const initial = [];
    if (preselectedTag) initial.push(preselectedTag);

    if (!hasBeenOnboarded) {
      // We changed the default tag name from "Seed List" to "Warm-up List"
      // Match either for backwards compatibility
      const defaultTag = tags.find((tag) => /(seed list|warm-up list)/i.test(tag.name));
      if (defaultTag && !initial.some((tag) => tag.id === defaultTag.id)) initial.push(defaultTag);

      // Tags have not arrived yet and we need one — stay unseeded so this retries.
      if (!defaultTag && !tags.length) return;
    }

    if (initial.length) updateNewImport({ selectedTags: initial });
    tagsSeededRef.current = true;
  }, [open, hasBeenOnboarded, preselectedTag, tags, updateNewImport]);

  const handleFileChange = useCallback(
    async (file) => {
      setImportFile(file);
      const { headers, data } = await parseCsvFile(file);
      updateNewImport({ fileName: file.name, csvData: data, csvHeaders: headers });
    },
    [updateNewImport],
  );

  const handleCompleteImport = async () => {
    if (!importFile) return;
    setLocalError(false);
    setLoading(true);
    try {
      const payload = {
        filename: importFile.name,
        fieldMappings: newImport.fieldMappings,
        importType: newImport.importType,
      };
      if (newImport.importType === IMPORT_TYPES.contacts) {
        payload.skipUpdate = newImport.skipUpdate;
        payload.sendRecurringEmails = newImport.sendRecurringEmails;
        payload.optInContacts = newImport.optInContacts;
        payload.tagIds = newImport.selectedTags.map((tag) => tag.id);
        // Per-row tags from the CSV's tags column, resolved to ids in MatchStep.
        // Unioned with tagIds server-side, not overridden by it.
        payload.tagValueMap = newImport.tagValueMap;
      }
      const uploadUrl = await createPresignedUploadUrl(payload);
      const arrayBuffer = await importFile.arrayBuffer();
      await uploadFile(uploadUrl, arrayBuffer, importFile.type);
      await getImports();
      goNext();
    } catch (err) {
      Sentry.captureException(err);
      setLocalError(getNetworkError(err));
    }
    setLoading(false);
  };

  const resetError = () => {
    if (localError) setLocalError(false);
    if (tagError) setTagError(false);
  };

  const handleClose = () => {
    setImportFile(null);
    resetNewImport();
    onClose();
  };

  const renderContent = () => {
    if (newImport.importType === IMPORT_TYPES.suppressionList) {
      switch (newImport.activeStep) {
        case 0:
          return <ImportTypeStep />;
        case 1:
          return <ImportStep importFile={importFile} onUploadFile={handleFileChange} />;
        case 2:
          return <MatchStep />;
        case 3:
          return <CompleteStep onComplete={handleCompleteImport} onCancel={handleClose} loading={loading} />;
        case 4:
          return <SuccessStep onClose={handleClose} />;
        default:
          return null;
      }
    }

    switch (newImport.activeStep) {
      case 0:
        return <ImportTypeStep />;
      case 1:
        return <ImportStep importFile={importFile} onUploadFile={handleFileChange} />;
      case 2:
        return <CategorizeStep />;
      case 3:
        return <TagStep />;
      case 4:
        return <MatchStep />;
      case 5:
        return <OnboardingStep />;
      case 6:
        return <CompleteStep onComplete={handleCompleteImport} onCancel={handleClose} loading={loading} />;
      case 7:
        return <SuccessStep onClose={handleClose} />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (newImport.activeStep > 0) {
      if (newImport.importType === IMPORT_TYPES.contacts) {
        return 'Import contacts';
      }
      if (newImport.importType === IMPORT_TYPES.suppressionList) {
        return 'Import suppression list';
      }
    }
    return 'Import contacts or suppression List';
  };

  const getUploadSteps = () => {
    if (newImport.importType === IMPORT_TYPES.contacts) {
      return IMPORT_CONTACTS_STEPS;
    }
    if (newImport.importType === IMPORT_TYPES.suppressionList) {
      return IMPORT_SUPPRESSION_LIST_STEPS;
    }
    return GENERAL_UPLOAD_STEPS;
  };

  const error = localError || tagError;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{ '.MuiPaper-root': { padding: 0 } }}
    >
      <div className="flex items-center justify-between border-b border-gray-50 px-6 py-3">
        <img alt="DonorSpring" src="dn-logo-64x64.png" className="mr-4 h-10" />

        <div className="flex-1">
          <h2 className="text-2xl font-semibold dark:text-white-100">{getTitle()}</h2>

          {newImport.activeStep < 6 && <Stepper steps={getUploadSteps()} activeStep={newImport.activeStep} />}
        </div>

        <Button title="Exit" onClick={handleClose} disabled={loading} />
      </div>

      <div className="relative m-10 flex-1">{renderContent()}</div>

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={resetError}
        severity="error"
      />
    </Dialog>
  );
};

FullScreenUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Tag to pre-apply to this import, e.g. from the onboarding donor group cards.
  preselectedTag: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
};

FullScreenUpload.defaultProps = {
  preselectedTag: null,
};

export default FullScreenUpload;
